import { SolomonLabsError } from "@solomon-labs/errors";

import type { TransactionStep } from "../types/transaction";

export type TransactionErrorType =
    | "WALLET_NOT_CONNECTED"
    | "SIGNER_NOT_READY"
    | "INVALID_AMOUNT"
    | "BUILD_FAILED"
    | "SIGN_FAILED"
    | "CONFIRM_FAILED"
    | "USER_REJECTED"
    | "INSUFFICIENT_FUNDS"
    | "UNKNOWN";

/**
 * Canonical user-facing message for a USER_REJECTED error. Exported so UI
 * code can detect wallet cancellation from `TransactionState.errorMessage`
 * without re-encoding the magic string.
 */
export const USER_CANCELLED_MESSAGE = "Transaction cancelled";

const USER_MESSAGES: Record<TransactionErrorType, string> = {
    WALLET_NOT_CONNECTED: "Please connect your wallet",
    SIGNER_NOT_READY: "Wallet is not ready. Please reconnect wallet",
    INVALID_AMOUNT: "Please enter a valid amount",
    BUILD_FAILED: "Failed to build transaction. Please try again",
    SIGN_FAILED: "Failed to sign transaction. Please try again",
    CONFIRM_FAILED: "Transaction failed to confirm. Please try again",
    USER_REJECTED: USER_CANCELLED_MESSAGE,
    INSUFFICIENT_FUNDS: "Insufficient funds for this transaction",
    UNKNOWN: "Transaction failed. Please try again"
};

/**
 * Patterns that indicate user rejection of transaction
 */
export const USER_REJECTION_PATTERNS = [
    "user rejected",
    "user denied",
    "user cancelled",
    "user canceled",
    "rejected the request",
    "cancelled by user",
    "canceled by user",
    "transaction was rejected",
    "transaction rejected",
    "request rejected",
    "signature request denied",
    "user declined"
];

/**
 * Patterns that indicate insufficient funds
 */
const INSUFFICIENT_FUNDS_PATTERNS = [
    "insufficient funds",
    "insufficient balance",
    "not enough balance",
    "insufficient lamports",
    "0x1" // Solana insufficient funds error code
];

/**
 * Extract all relevant error messages from an error, including nested originalError
 */
function getErrorMessages(error: unknown): string[] {
    const messages: string[] = [];

    if (error instanceof Error) {
        messages.push(error.message.toLowerCase());
    } else if (typeof error === "string") {
        messages.push(error.toLowerCase());
    }

    // Check for originalError property (used by @solana/connector TransactionError)
    const errorWithOriginal = error as { originalError?: unknown };
    if (errorWithOriginal?.originalError) {
        if (typeof errorWithOriginal.originalError === "string") {
            messages.push(errorWithOriginal.originalError.toLowerCase());
        } else if (errorWithOriginal.originalError instanceof Error) {
            messages.push(errorWithOriginal.originalError.message.toLowerCase());
        }
    }

    // Check for cause property (standard Error cause)
    const errorWithCause = error as { cause?: unknown };
    if (errorWithCause?.cause) {
        if (typeof errorWithCause.cause === "string") {
            messages.push(errorWithCause.cause.toLowerCase());
        } else if (errorWithCause.cause instanceof Error) {
            messages.push(errorWithCause.cause.message.toLowerCase());
        }
    }

    return messages;
}

/**
 * Detect error type from caught error message
 */
function detectErrorType(error: unknown, step?: TransactionStep): TransactionErrorType {
    const messages = getErrorMessages(error);

    // Check for user rejection in all messages
    for (const message of messages) {
        for (const pattern of USER_REJECTION_PATTERNS) {
            if (message.includes(pattern)) {
                return "USER_REJECTED";
            }
        }
    }

    // Check for insufficient funds in all messages
    for (const message of messages) {
        for (const pattern of INSUFFICIENT_FUNDS_PATTERNS) {
            if (message.includes(pattern)) {
                return "INSUFFICIENT_FUNDS";
            }
        }
    }

    // Map to step-specific error type
    if (step === "building") {
        return "BUILD_FAILED";
    }
    if (step === "signing") {
        return "SIGN_FAILED";
    }
    if (step === "confirming") {
        return "CONFIRM_FAILED";
    }

    return "UNKNOWN";
}

/**
 * Transaction-specific error for transaction hooks.
 *
 * Provides:
 * - Typed error categorization via `type`
 * - User-friendly messages via `userMessage`
 * - Transaction step tracking via `step`
 * - Original error preservation for debugging
 *
 * @example
 * ```ts
 * // Validation error
 * const error = TransactionError.validation("WALLET_NOT_CONNECTED", "Wallet not connected");
 *
 * // Wrap caught exception
 * catch (error) {
 *   const txError = TransactionError.fromCaught(error, "confirming");
 *   console.log(txError.userMessage); // "Transaction cancelled" if user rejected
 * }
 * ```
 */
export class TransactionError extends SolomonLabsError {
    readonly category = "TRANSACTION";
    readonly type: TransactionErrorType;
    readonly step?: TransactionStep;

    private constructor(type: TransactionErrorType, message: string, step?: TransactionStep, originalError?: unknown) {
        super(message, originalError);
        this.type = type;
        this.step = step;
    }

    /**
     * User-friendly error message suitable for display in UI
     */
    get userMessage(): string {
        return USER_MESSAGES[this.type];
    }

    /**
     * Create a validation error (pre-transaction checks)
     */
    static validation(type: TransactionErrorType, message: string): TransactionError {
        return new TransactionError(type, message);
    }

    /**
     * Create a TransactionError from a caught exception.
     * Automatically detects user rejection and insufficient funds from error messages.
     *
     * @param error - The caught error
     * @param step - The transaction step where the error occurred
     */
    static fromCaught(error: unknown, step?: TransactionStep): TransactionError {
        const detectedType = detectErrorType(error, step);
        const message = error instanceof Error ? error.message : "Transaction failed";

        return new TransactionError(detectedType, message, step, error);
    }
}
